import type {
  CommissionStatus,
  GoogleEvent,
  Interaction,
  Payment,
  Prestation,
  Prospect,
  RdvOutcome,
  Sale,
  Todo,
  Webinar,
  WebinarAttendee,
} from "@shared/schema";
import type { CallStatsResponse } from "@shared/call-stats";
import type { OutcomeChoice } from "@shared/outcome";
import type {
  TodayData,
  WebinarImportBody,
  WebinarImportResult,
  WebinarPreview,
} from "@shared/webinar-api";

export type EnrichedPayment = Payment & {
  effectiveCommissionStatus:
    | "en_attente_retractation"
    | "du"
    | "paye"
    | "annule";
};

export type SaleWithRelations = Sale & {
  prospect: Prospect | null;
  payments: EnrichedPayment[];
};

export interface DashboardData {
  summary: {
    pendingRetractation: number;
    due: number;
    paid: number;
    cancelled: number;
    totalSalesVolume: number;
    totalCommissionLifetime: number;
    closedCount: number;
    prospectsTotal: number;
    closingRatio: number;
  };
  followUps: Array<Prospect & { overdue: boolean }>;
  byStatus: Record<string, number>;
  recentSales: Array<
    Sale & {
      prospect: Prospect | null;
      commissionStatus: CommissionStatus;
      inAdvance: boolean;
    }
  >;
}

/** Message lisible depuis le corps d'erreur : `{ error: string }` ou `{ success:false, error: ZodError }`. */
function errorMessage(data: unknown, status: number): string {
  const err = (data as { error?: unknown } | null)?.error;
  if (typeof err === "string" && err) return err;
  const issues =
    (err as { issues?: Array<{ message?: unknown }> } | undefined)?.issues ??
    (err as { error?: { issues?: Array<{ message?: unknown }> } } | undefined)?.error?.issues;
  const first = issues?.find((i) => typeof i?.message === "string" && i.message);
  if (first) return first.message as string;
  return err !== undefined ? "Données invalides" : `HTTP ${status}`;
}

async function request<T>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(init.headers ?? {}),
      },
    });
  } catch {
    throw new Error("Connexion impossible, réessaie dans un instant.");
  }
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      message = errorMessage(await res.json(), res.status);
    } catch {}
    // On a 401 outside the login page, kick the user back to /login
    if (
      res.status === 401 &&
      !window.location.pathname.startsWith("/login")
    ) {
      window.location.assign("/login");
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // Auth
  me: () => request<{ authed: boolean; appName: string }>("/api/auth/me"),
  login: (password: string) =>
    request<{ ok: true }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  logout: () =>
    request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  // Prospects
  listProspects: (params: { status?: string; q?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.status) sp.set("status", params.status);
    if (params.q) sp.set("q", params.q);
    const qs = sp.toString();
    return request<{ prospects: Prospect[] }>(
      `/api/prospects${qs ? `?${qs}` : ""}`,
    );
  },
  getProspect: (id: number) =>
    request<{
      prospect: Prospect;
      interactions: Interaction[];
      sales: Sale[];
      attendee: WebinarAttendee | null;
      /** RDV du prospect, du plus récent au plus ancien (annulés compris). */
      events: GoogleEvent[];
    }>(`/api/prospects/${id}`),
  setOutcome: (
    id: number,
    data: {
      choice: OutcomeChoice;
      eventId?: number;
      followUpAt?: string;
      followUpTime?: string;
      blocker?: string;
      note?: string;
      lossReason?: string;
      /** Prospect déjà acheté : changement d'issue confirmé. */
      force?: boolean;
    },
  ) =>
    request<{ prospect: Prospect; interaction: Interaction; touchedProspect: boolean }>(
      `/api/prospects/${id}/outcome`,
      { method: "POST", body: JSON.stringify(data) },
    ),
  createProspect: (data: Partial<Prospect>) =>
    request<{ prospect: Prospect }>("/api/prospects", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateProspect: (id: number, data: Partial<Prospect>) =>
    request<{ prospect: Prospect }>(`/api/prospects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteProspect: (id: number) =>
    request<{ ok: true }>(`/api/prospects/${id}`, { method: "DELETE" }),

  // Réglages
  settings: () => request<{ settings: Record<string, string> }>("/api/settings"),
  updateSettings: (data: { owner_emails?: string; calendly_booking_url?: string }) =>
    request<{ settings: Record<string, string> }>("/api/settings", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // Webinaires
  previewWebinar: (data: { url: string; csv?: string }) =>
    request<WebinarPreview>("/api/webinars/preview", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  importWebinar: (data: WebinarImportBody) =>
    request<WebinarImportResult>("/api/webinars/import", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  listWebinars: () => request<{ webinars: Webinar[] }>("/api/webinars"),

  // Aujourd'hui
  today: (date?: string) =>
    request<TodayData>(`/api/today${date ? `?date=${encodeURIComponent(date)}` : ""}`),

  // Statistiques d'appels
  callStats: (params: { from: string; to: string; group?: "week" }) => {
    const q = new URLSearchParams({ from: params.from, to: params.to });
    if (params.group) q.set("group", params.group);
    return request<CallStatsResponse>(`/api/stats/calls?${q.toString()}`);
  },

  // Sales
  listSales: () => request<{ sales: SaleWithRelations[] }>("/api/sales"),
  getSale: (id: number) =>
    request<{
      sale: Sale;
      prospect: Prospect | null;
      payments: EnrichedPayment[];
    }>(`/api/sales/${id}`),
  createSale: (data: {
    prospectId: number;
    product: "power_coaching" | "accelerateur";
    totalAmount: number;
    commissionRate: number;
    closedAt: string;
    paymentPlan: number;
    notes?: string | null;
  }) =>
    request<{ sale: Sale }>("/api/sales", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateSale: (
    id: number,
    data: { status?: "actif" | "rembourse" | "annule"; notes?: string | null },
  ) =>
    request<{ sale: Sale }>(`/api/sales/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // Payments
  updatePayment: (
    id: number,
    data: Partial<{
      status: "due" | "paye" | "rembourse";
      paidDate: string | null;
      commissionStatus: "en_attente_retractation" | "du" | "paye" | "annule";
      commissionPaidDate: string | null;
    }>,
  ) =>
    request<{ payment: Payment }>(`/api/payments/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // Interactions
  createInteraction: (data: {
    prospectId: number;
    kind: "note" | "appel" | "message" | "email" | "rdv" | "relance";
    body: string;
    occurredAt?: string;
  }) =>
    request<{ interaction: Interaction }>("/api/interactions", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  deleteInteraction: (id: number) =>
    request<{ ok: true }>(`/api/interactions/${id}`, { method: "DELETE" }),

  // Dashboard
  dashboard: (params: { from?: string; to?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.from) sp.set("from", params.from);
    if (params.to) sp.set("to", params.to);
    const qs = sp.toString();
    return request<DashboardData>(`/api/dashboard${qs ? `?${qs}` : ""}`);
  },

  // Google Calendar
  googleStatus: () =>
    request<{
      connected: boolean;
      userEmail?: string | null;
      calendarId?: string;
      filterPattern?: string;
      lastSyncAt?: string | null;
      lastSyncError?: string | null;
      lastSyncCount?: number | null;
      connectedAt?: string;
    }>("/api/google/status"),
  googleSync: () =>
    request<{
      ok: boolean;
      fetched: number;
      created: number;
      updated: number;
      prospectsCreated: number;
      prospectsLinked: number;
    }>("/api/google/sync", { method: "POST" }),
  googleUpdateConfig: (data: {
    filterPattern?: string;
    calendarId?: string;
  }) =>
    request<{ ok: true }>("/api/google/config", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  googleDisconnect: () =>
    request<{ ok: true }>("/api/google/disconnect", { method: "POST" }),
  googleEvents: (params: { upcoming?: boolean; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.upcoming) sp.set("upcoming", "1");
    if (params.limit) sp.set("limit", String(params.limit));
    const qs = sp.toString();
    return request<{
      events: Array<GoogleEvent & { prospect: Prospect | null }>;
    }>(`/api/google/events${qs ? `?${qs}` : ""}`);
  },
  setEventOutcome: (id: number, outcome: RdvOutcome | null) =>
    request<{ ok: true; event: GoogleEvent }>(`/api/google/events/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ outcome }),
    }),

  // Prestations
  listPrestations: () =>
    request<{ prestations: Prestation[] }>("/api/prestations"),
  getPrestation: (id: number) =>
    request<{ prestation: Prestation }>(`/api/prestations/${id}`),
  createPrestation: (data: {
    clientName: string;
    clientEmail?: string | null;
    description?: string | null;
    amount: number;
    invoicedAt: string;
    dueAt?: string | null;
    notes?: string | null;
  }) =>
    request<{ prestation: Prestation }>("/api/prestations", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updatePrestation: (id: number, data: Partial<Prestation>) =>
    request<{ prestation: Prestation }>(`/api/prestations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deletePrestation: (id: number) =>
    request<{ ok: true }>(`/api/prestations/${id}`, { method: "DELETE" }),
  prestationsSummary: (params: { from?: string; to?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.from) sp.set("from", params.from);
    if (params.to) sp.set("to", params.to);
    const qs = sp.toString();
    return request<{
      summary: { invoiced: number; pending: number; paid: number };
    }>(`/api/prestations/summary/totals${qs ? `?${qs}` : ""}`);
  },

  // To-Do
  listTodos: () => request<{ todos: Todo[] }>("/api/todos"),
  createTodo: (data: { title: string; dueAt?: string | null }) =>
    request<{ todo: Todo }>("/api/todos", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateTodo: (
    id: number,
    data: { title?: string; dueAt?: string | null; done?: boolean },
  ) =>
    request<{ todo: Todo }>(`/api/todos/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteTodo: (id: number) =>
    request<{ ok: true }>(`/api/todos/${id}`, { method: "DELETE" }),
};
